import { ChannelType, Institution, storageGet, storageRemove, storageSet } from '@faceup/utils'
import { type InferType, yup } from '@faceup/validation'
import { StorageKeys } from './useStorage'

/**
 * Single source for the app's persisted USER_DATA blob (app client only,
 * localStorage). AppLockProvider (authPin, settings) and AccessKeyProvider
 * (institution) each persist their slice through these helpers; the on-disk
 * shape stays 1:1 with the old app.
 */
const schema = yup
  .object({
    institution: yup
      .object({
        institutionId: yup.string().required(),
        defaultFormId: yup.string().required(),
        type: yup.string().oneOf(Object.values(Institution)).required(),
        channelType: yup.string().oneOf(Object.values(ChannelType)).required(),
        shortLink: yup.string().required(),
        password: yup.string(),
      })
      .nullable(),
    authPin: yup.string().nullable(),
    settings: yup
      .object({
        biometrics: yup.boolean().nullable(),
      })
      .nullable(),
  })
  .required()

export type UserSavedData = InferType<typeof schema>

export const getUserData = (): UserSavedData | null => {
  const item = storageGet('localStorage', StorageKeys.User)
  if (!item) {
    return null
  }
  try {
    const data = JSON.parse(item)
    if (!schema.isValidSync(data)) {
      storageRemove('localStorage', StorageKeys.User)
      return null
    }
    return data as UserSavedData
  } catch {
    storageRemove('localStorage', StorageKeys.User)
    return null
  }
}

const writeUserData = (data: UserSavedData) =>
  storageSet('localStorage', StorageKeys.User, JSON.stringify(data))

/** Read-modify-write a single slice so the other provider's slice is preserved. */
export const persistUserDataSlice = (slice: Partial<UserSavedData>) => {
  const current = getUserData() ?? { institution: null, authPin: null, settings: null }
  writeUserData({ ...current, ...slice })
}

export const clearUserData = () => storageRemove('localStorage', StorageKeys.User)
